import { Suspense, useCallback, useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { displayName, useCan, useSession } from '../auth/session.js'
import { initials } from '../domain/format.js'
import { useI18n } from '../i18n/context.js'
import { paths, siteSectionOf } from '../routes.js'
import { useSiteWorkspace } from '../workspace/site.js'
import { useTenantAdmin } from '../workspace/tenant-admin.js'
import { BrandMark } from '../components/BrandMark.js'
import { ReleaseProtocol } from '../components/ReleaseProtocol.js'
import { PublishDialog } from '../screens/PublishDialog.js'
import { MenuButton, type MenuEntry } from '../ui/overlays.js'
import { Banner, Button, Icon, IconButton, LinkButton, PageSkeleton, cx } from '../ui/primitives.js'
import type { IconName } from '../ui/icons.js'
import { GlobalSearch } from './GlobalSearch.js'
import { SITE_GROUPS, SITE_ITEMS } from './nav.js'
import { SiteSwitcher } from './SiteSwitcher.js'
import { TenantSwitcher } from './TenantSwitcher.js'
import { readRailPreference, saveRailPreference, useMediaQuery, useThemeMode, type ThemeMode } from './preferences.js'

interface NavItem {
  key: string
  label: string
  icon: IconName
  to: string
  active: boolean
}

const THEME_ICONS: Record<ThemeMode, IconName> = { light: 'light_mode', dark: 'dark_mode', system: 'contrast' }

/**
 * The frame every signed-in page lives in, the Google Workspace way: a top
 * app bar (menu, product, site switcher, search, publish, preferences,
 * account) and a navigation drawer. On a wide screen the drawer is always
 * there and folds into a rail; on a phone it is a modal sheet.
 */
export function AppShell({ children }: { children: ReactNode }) {
  const { t, locale, setLocale } = useI18n()
  const { state, signOut } = useSession()
  const { siteId, site, siteInUrl, reload, operator, tenantEpoch, tenantNotice } = useSiteWorkspace()
  const { canManagePeople, canManageKeys } = useTenantAdmin()
  const location = useLocation()
  const wide = useMediaQuery('(min-width: 1024px)')
  const [modalOpen, setModalOpen] = useState(false)
  const [rail, setRail] = useState(readRailPreference)
  const [searchExpanded, setSearchExpanded] = useState(false)
  const [themeMode, setThemeMode] = useThemeMode()
  const [publishing, setPublishing] = useState(false)
  const person = state.status === 'signed-in' ? state.person : null
  const canWrite = useCan('cms:content:write')
  const canPublish = useCan('cms:publish')
  const pathname = location.pathname
  const section = siteSectionOf(pathname)

  useEffect(() => setModalOpen(false), [pathname])
  useEffect(() => {
    if (!modalOpen) return
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') setModalOpen(false) }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [modalOpen])

  const toggleDrawer = () => {
    if (wide) {
      setRail((current) => { saveRailPreference(!current); return !current })
    } else setModalOpen((current) => !current)
  }

  const overviewItem: NavItem | null = siteId ? { key: 'overview', label: t('nav.overview'), icon: 'dashboard', to: paths.site(siteId), active: siteInUrl && section === null } : null
  const siteGroups = siteId ? SITE_GROUPS.map((group) => ({
    key: group.key,
    label: t(group.labelKey),
    items: group.sections.map((name): NavItem => ({ key: name, label: t(SITE_ITEMS[name].labelKey), icon: SITE_ITEMS[name].icon, to: paths.siteSection(siteId, name), active: siteInUrl && section === name })),
  })) : []
  const tenantItems: NavItem[] = [
    { key: 'knowledge', label: t('nav.knowledge'), icon: 'psychology', to: paths.knowledge(), active: pathname.startsWith('/conoscenza') },
    ...(canManagePeople ? [{ key: 'people', label: t('nav.people'), icon: 'group' as IconName, to: paths.people(), active: pathname.startsWith('/persone') }] : []),
    ...(canManageKeys ? [{ key: 'keys', label: t('nav.apiKeys'), icon: 'key' as IconName, to: paths.apiKeys(), active: pathname.startsWith('/chiavi-api') }] : []),
    ...(operator ? [{ key: 'operator', label: t('nav.operator'), icon: 'admin_panel_settings' as IconName, to: paths.operator(), active: pathname.startsWith('/operatore') }] : []),
    { key: 'releases', label: t('release.notes'), icon: 'new_releases', to: paths.releaseNotes(), active: pathname.startsWith('/note-di-rilascio') },
  ]
  const collapsed = wide && rail

  const onPublished = useCallback(() => { void reload() }, [reload])

  const themeItems: MenuEntry[] = (['light', 'dark', 'system'] as const).map((mode) => ({
    key: mode,
    label: t(`theme.mode.${mode}`),
    icon: themeMode === mode ? 'check' : THEME_ICONS[mode],
    onSelect: () => setThemeMode(mode),
  }))
  const languageItems: MenuEntry[] = (['it', 'en'] as const).map((code) => ({
    key: code,
    label: t(`language.${code}Native`),
    icon: locale === code ? 'check' : 'translate',
    onSelect: () => setLocale(code),
  }))
  const accountItems: MenuEntry[] = [
    { key: 'releases', label: t('release.notes'), icon: 'new_releases', to: paths.releaseNotes() },
    { key: 'logout', label: t('auth.logout'), icon: 'logout', onSelect: () => void signOut(), divider: true },
  ]

  return <div className={cx('app', collapsed && 'is-rail', modalOpen && 'is-drawer-open', searchExpanded && 'is-searching')}>
    <a className="skip-link" href="#main">{t('shell.skipToContent')}</a>
    <header className="topbar">
      <IconButton icon="menu" label={wide ? (rail ? t('shell.expandMenu') : t('shell.collapseMenu')) : (modalOpen ? t('workspace.closeMenu') : t('workspace.openMenu'))} aria-expanded={wide ? !rail : modalOpen} aria-controls="app-drawer" onClick={toggleDrawer} className="topbar-menu"/>
      <Link to={paths.home()} className="brand" aria-label={t('shell.home')}>
        <BrandMark />
      </Link>
      <TenantSwitcher/>
      <SiteSwitcher/>
      <GlobalSearch onExpandedChange={setSearchExpanded}/>
      <div className="topbar-actions">
        {siteInUrl && site && canPublish && <Button variant="tonal" icon="rocket_launch" className="topbar-publish" onClick={() => setPublishing(true)} title={t('shell.publishHelp')}>{t('list.publishSite')}</Button>}
        <MenuButton label={t('workspace.toggleTheme')} icon={THEME_ICONS[themeMode]} items={themeItems}/>
        <MenuButton label={t('workspace.language')} items={languageItems} trigger={(props) => <button type="button" className="text-icon-btn" aria-label={t('workspace.language')} title={t('workspace.language')} {...props}>{locale.toUpperCase()}</button>}/>
        {person && <MenuButton
          label={t('shell.account')}
          items={accountItems}
          header={<div className="account-card">
            <span className="avatar avatar-large">{person.picture ? <img src={person.picture} alt="" width={48} height={48}/> : initials(displayName(person))}</span>
            <strong>{displayName(person)}</strong>
            {person.email && <span className="muted">{person.email}</span>}
            <span className="account-roles">{[...person.roles, ...person.appRoles].slice(0, 4).map((role) => <span key={role} className="chip chip-static">{role}</span>)}</span>
          </div>}
          trigger={(props) => <button type="button" className="avatar-btn" aria-label={t('shell.accountOf', { name: displayName(person) })} title={displayName(person)} {...props}>
            <span className="avatar">{person.picture ? <img src={person.picture} alt="" width={32} height={32}/> : initials(displayName(person))}</span>
          </button>}
        />}
      </div>
    </header>

    <button type="button" className="drawer-scrim" aria-hidden="true" tabIndex={-1} onClick={() => setModalOpen(false)}/>
    <aside id="app-drawer" className="drawer" aria-label={t('shell.navigation')}>
      <div className="drawer-scroll">
        {canWrite && siteId && <LinkButton variant="filled" icon="edit" className="compose" to={paths.newContent(siteId)} title={t('list.newContent')}>{t('list.newContent')}</LinkButton>}
        <nav aria-label={t('shell.navigation')}>
          <NavLink item={{ key: 'home', label: t('nav.home'), icon: 'home', to: paths.home(), active: pathname === '/' }} collapsed={collapsed}/>
          {site && <p className="drawer-heading drawer-site" title={site.name}>{site.name}</p>}
          {overviewItem && <NavLink item={overviewItem} collapsed={collapsed}/>}
          {siteGroups.map((group) => <div key={group.key} role="group" aria-label={group.label} className="nav-group">
            <p className="drawer-heading" aria-hidden="true">{group.label}</p>
            {group.items.map((item) => <NavLink key={item.key} item={item} collapsed={collapsed}/>)}
          </div>)}
          <hr className="drawer-divider"/>
          <div role="group" aria-label={t('navGroup.client')} className="nav-group">
            <p className="drawer-heading" aria-hidden="true">{t('navGroup.client')}</p>
            {tenantItems.map((item) => <NavLink key={item.key} item={item} collapsed={collapsed}/>)}
          </div>
        </nav>
      </div>
    </aside>

    <main id="main" className="main" tabIndex={-1}>
      {/* Keyed on the client: switching it starts every screen again from nothing, with the new X-Tenant. */}
      <div className="main-inner" key={tenantEpoch}>
        {tenantNotice && <Banner tone="warning" title={t('tenantSwitcher.resetTitle')}>{t('tenantSwitcher.reset')}</Banner>}
        <Suspense fallback={<PageSkeleton label={t('common.loading')}/>}>{children}</Suspense>
      </div>
      <ReleaseProtocol/>
    </main>
    {publishing && site && <PublishDialog siteId={site.id} siteName={site.name} onClose={() => setPublishing(false)} onPublished={onPublished}/>}
  </div>
}

function NavLink({ item, collapsed }: { item: NavItem; collapsed: boolean }) {
  return <Link to={item.to} className={cx('nav-item', item.active && 'is-active')} aria-current={item.active ? 'page' : undefined} title={collapsed ? item.label : undefined}>
    <Icon name={item.icon} filled={item.active}/>
    <span className="nav-label">{item.label}</span>
  </Link>
}
